import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { ticketsAPI } from '@/lib/api/services/tickets'
import type {
  CreateTicketRequest,
  UpdateTicketRequest,
  CreateTicketReplyRequest,
} from '@/types/ticket'

// ========== ADMIN HOOKS ==========

export function useAdminTickets(params: Record<string, any> = {}) {
  return useQuery({
    queryKey: ['admin', 'tickets', 'list', params],
    queryFn: async () => {
      const res = await ticketsAPI.admin.list(params)
      if (!res.success) {
        throw new Error(res.error || 'Failed to fetch tickets')
      }
      return res.data!
    },
  })
}

export function useAdminTicket(id: number | string) {
  return useQuery({
    queryKey: ['admin', 'tickets', 'detail', id],
    queryFn: async () => {
      const res = await ticketsAPI.admin.get(id)
      if (!res.success) throw new Error(res.error || 'Failed to fetch ticket')
      return res.data!
    },
    enabled: !!id,
    // No polling - real-time updates via WebSocket events (see useTicketWebSocket hook)
  })
}

export function useAdminTicketReplies(id: number | string) {
  return useQuery({
    queryKey: ['admin', 'tickets', 'replies', id],
    queryFn: async () => {
      const res = await ticketsAPI.admin.getReplies(id)
      if (!res.success) throw new Error(res.error || 'Failed to fetch replies')
      return res.data!
    },
    enabled: !!id,
  })
}

export function useAdminTicketMutations() {
  const queryClient = useQueryClient()

  const createTicket = useMutation({
    mutationFn: async (data: CreateTicketRequest) => {
      const res = await ticketsAPI.admin.create(data)
      if (!res.success) throw new Error(res.error || 'Failed to create ticket')
      return res.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'tickets'] })
    },
  })

  const updateTicket = useMutation({
    mutationFn: async ({
      id,
      data,
    }: {
      id: number | string
      data: UpdateTicketRequest
    }) => {
      const res = await ticketsAPI.admin.update(id, data)
      if (!res.success) throw new Error(res.error || 'Failed to update ticket')
      return res.data
    },
    onSuccess: (response, { id }) => {
      queryClient.invalidateQueries({
        queryKey: ['admin', 'tickets', 'detail', id],
      })
      queryClient.invalidateQueries({ queryKey: ['admin', 'tickets', 'list'] })
    },
  })

  const assignTicket = useMutation({
    mutationFn: async ({
      id,
      data,
    }: {
      id: number | string
      data: {
        assignedToUserId?: number
        assignedToTeamId?: number
        reason?: string
      }
    }) => {
      const res = await ticketsAPI.admin.assign(id, data)
      if (!res.success) throw new Error(res.error || 'Failed to assign ticket')
      return res.data
    },
    onSuccess: (response, { id }) => {
      queryClient.invalidateQueries({
        queryKey: ['admin', 'tickets', 'detail', id],
      })
      queryClient.invalidateQueries({ queryKey: ['admin', 'tickets', 'list'] })
    },
  })

  const resolveTicket = useMutation({
    mutationFn: async (id: number | string) => {
      const res = await ticketsAPI.admin.resolve(id)
      if (!res.success) throw new Error(res.error || 'Failed to resolve ticket')
      return res.data
    },
    onSuccess: (response, id) => {
      queryClient.invalidateQueries({
        queryKey: ['admin', 'tickets', 'detail', id],
      })
      queryClient.invalidateQueries({ queryKey: ['admin', 'tickets', 'list'] })
    },
  })

  const closeTicket = useMutation({
    mutationFn: async (id: number | string) => {
      const res = await ticketsAPI.admin.close(id)
      if (!res.success) throw new Error(res.error || 'Failed to close ticket')
      return res.data
    },
    onSuccess: (response, id) => {
      queryClient.invalidateQueries({
        queryKey: ['admin', 'tickets', 'detail', id],
      })
      queryClient.invalidateQueries({ queryKey: ['admin', 'tickets', 'list'] })
    },
  })

  const createReply = useMutation({
    mutationFn: async ({
      ticketId,
      data,
    }: {
      ticketId: number | string
      data: CreateTicketReplyRequest
    }) => {
      const res = await ticketsAPI.admin.reply(ticketId, data)
      if (!res.success) throw new Error(res.error || 'Failed to send reply')
      return res.data
    },
    onSuccess: (response, { ticketId }) => {
      queryClient.invalidateQueries({
        queryKey: ['admin', 'tickets', 'replies', ticketId],
      })
      queryClient.invalidateQueries({
        queryKey: ['admin', 'tickets', 'detail', ticketId],
      })
    },
  })

  return {
    createTicket,
    updateTicket,
    assignTicket,
    resolveTicket,
    closeTicket,
    createReply,
  }
}

export function useAdminTicketGroups() {
  return useQuery({
    queryKey: ['admin', 'ticket-groups'],
    queryFn: async () => {
      const res = await ticketsAPI.groups.listWithCategories()
      if (!res.success) {
        throw new Error(res.error || 'Failed to fetch ticket groups')
      }
      return res.data!
    },
  })
}

// Alias for useAdminTicketGroups
export const useTicketGroups = useAdminTicketGroups

export function useTicketGroupMutations() {
  const queryClient = useQueryClient()

  const createGroup = useMutation({
    mutationFn: async (data: { name: string; description?: string }) => {
      const res = await ticketsAPI.groups.create({
        siteId: 0, // Will be set by middleware
        name: data.name,
        slug: data.name.toLowerCase().replace(/\s+/g, '-'),
        description: data.description || null,
        displayOrder: 0,
        isActive: true,
      } as any)
      if (!res.success) throw new Error(res.error || 'Failed to create group')
      return res.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'ticket-groups'] })
    },
  })

  const updateGroup = useMutation({
    mutationFn: async ({
      id,
      data,
    }: {
      id: number
      data: { name?: string; description?: string }
    }) => {
      const res = await ticketsAPI.groups.update(id, data)
      if (!res.success) throw new Error(res.error || 'Failed to update group')
      return res.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'ticket-groups'] })
    },
  })

  const deleteGroup = useMutation({
    mutationFn: async (id: number) => {
      const res = await ticketsAPI.groups.delete(id)
      if (!res.success) throw new Error(res.error || 'Failed to delete group')
      return res.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'ticket-groups'] })
    },
  })

  const createCategory = useMutation({
    mutationFn: async (data: {
      ticketGroupId: number
      name: string
      description?: string
    }) => {
      const res = await ticketsAPI.groups.createCategory({
        siteId: 0, // Will be set by middleware
        groupId: data.ticketGroupId, // Backend expects groupId, not ticketGroupId
        name: data.name,
        slug: data.name
          .toLowerCase()
          .replace(/\s+/g, '-')
          .replace(/[^a-z0-9-]/g, ''),
        description: data.description || null,
        displayOrder: 0,
        isActive: true,
      } as any)
      if (!res.success)
        throw new Error(res.error || 'Failed to create category')
      return res.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'ticket-groups'] })
    },
  })

  const updateCategory = useMutation({
    mutationFn: async ({
      id,
      data,
    }: {
      id: number
      data: {
        ticketGroupId?: number
        name?: string
        description?: string
        displayOrder?: number
      }
    }) => {
      // Transform ticketGroupId to groupId for backend
      const payload: any = { ...data }
      if (data.ticketGroupId !== undefined) {
        payload.groupId = data.ticketGroupId
        delete payload.ticketGroupId
      }
      // Generate slug if name is being updated
      if (data.name) {
        payload.slug = data.name
          .toLowerCase()
          .replace(/\s+/g, '-')
          .replace(/[^a-z0-9-]/g, '')
      }

      const res = await ticketsAPI.groups.updateCategory(id, payload)
      if (!res.success)
        throw new Error(res.error || 'Failed to update category')
      return res.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'ticket-groups'] })
    },
  })

  const deleteCategory = useMutation({
    mutationFn: async (id: number) => {
      const res = await ticketsAPI.groups.deleteCategory(id)
      if (!res.success)
        throw new Error(res.error || 'Failed to delete category')
      return res.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'ticket-groups'] })
    },
  })

  return {
    createGroup,
    updateGroup,
    deleteGroup,
    createCategory,
    updateCategory,
    deleteCategory,
  }
}

// ========== AGENT HOOKS ==========

export function useAgentTickets(params: Record<string, any> = {}) {
  return useQuery({
    queryKey: ['agent', 'tickets', 'list', params],
    queryFn: async () => {
      const res = await ticketsAPI.agent.list(params)
      if (!res.success) {
        throw new Error(res.error || 'Failed to fetch tickets')
      }
      return res.data!
    },
  })
}

export function useAgentTicket(id: number | string) {
  return useQuery({
    queryKey: ['agent', 'tickets', 'detail', id],
    queryFn: async () => {
      const res = await ticketsAPI.agent.get(id)
      if (!res.success) throw new Error(res.error || 'Failed to fetch ticket')
      return res.data!
    },
    enabled: !!id,
    // No polling - real-time updates via WebSocket events (see useTicketWebSocket hook)
  })
}

export function useAgentTicketMutations() {
  const queryClient = useQueryClient()

  const createTicket = useMutation({
    mutationFn: async (data: CreateTicketRequest) => {
      const res = await ticketsAPI.agent.create(data)
      if (!res.success) throw new Error(res.error || 'Failed to create ticket')
      return res.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['agent', 'tickets'] })
    },
  })

  const updateTicket = useMutation({
    mutationFn: async ({
      id,
      data,
    }: {
      id: number | string
      data: UpdateTicketRequest
    }) => {
      const res = await ticketsAPI.agent.update(id, data)
      if (!res.success) throw new Error(res.error || 'Failed to update ticket')
      return res.data
    },
    onSuccess: (response, { id }) => {
      queryClient.invalidateQueries({
        queryKey: ['agent', 'tickets', 'detail', id],
      })
      queryClient.invalidateQueries({ queryKey: ['agent', 'tickets', 'list'] })
    },
  })

  const createReply = useMutation({
    mutationFn: async ({
      ticketId,
      data,
    }: {
      ticketId: number | string
      data: CreateTicketReplyRequest
    }) => {
      const res = await ticketsAPI.agent.reply(ticketId, data)
      if (!res.success) throw new Error(res.error || 'Failed to send reply')
      return res.data
    },
    onSuccess: (response, { ticketId }) => {
      queryClient.invalidateQueries({
        queryKey: ['agent', 'tickets', 'detail', ticketId],
      })
    },
  })

  return {
    createTicket,
    updateTicket,
    createReply,
  }
}

// ========== PUBLIC/GUEST HOOKS ==========

export function usePublicTicket(token: string) {
  return useQuery({
    queryKey: ['public', 'tickets', token],
    queryFn: async () => {
      const res = await ticketsAPI.public.getByToken(token)
      if (!res.success) throw new Error(res.error || 'Failed to fetch ticket')
      return res.data!
    },
    enabled: !!token,
  })
}

export function usePublicTicketMutations() {
  const queryClient = useQueryClient()

  const createReply = useMutation({
    mutationFn: async ({
      token,
      data,
    }: {
      token: string
      data: { content: string; attachments?: any[] }
    }) => {
      const res = await ticketsAPI.public.reply(token, data)
      if (!res.success) throw new Error(res.error || 'Failed to send reply')
      return res.data
    },
    onSuccess: (response, { token }) => {
      queryClient.invalidateQueries({ queryKey: ['public', 'tickets', token] })
    },
  })

  return {
    createReply,
  }
}
